import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Modal } from '@material-ui/core';
import useApi, { GET_STATISTIC_TYPES } from 'hooks/useApi';
import LoadingSpinner from 'components/LoadingSpinner';
import { Message, ThemeButton } from 'components/shared';
import ModalItem from './ModalItem';
import { orderActiveItems } from '../utils';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.white,
    position: 'absolute',
    width: '400px',
    height: '500px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: '0',
    borderRadius: '4px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end'
  },
  itemsContainer: {
    ...theme.scrollStyles,
    maxHeight: '450px',
    overflow: 'auto',
    marginBottom: '16px'
  }
}));

const ActiveAttributesModal = ({ attributes, isOpen, onClose, onAttributesUpdateSubmit }) => {
  const [activeAttributes, setActiveAttributes] = useState([]);
  const [state] = useApi(GET_STATISTIC_TYPES());
  const { data: response, isLoading, isError, error } = state;

  const classes = useStyles();

  useEffect(() => {
    if (!response || isLoading || isError) return;

    const mappedAttributes = response.data.map(attr => ({
      ...attr,
      isActive: attributes.some(a => a.attribute === attr.name)
    }));
    setActiveAttributes(orderActiveItems(mappedAttributes, 'itemId'));
  }, [response, isLoading, isError, attributes, onClose]);

  const onAttributeChecked = (attribute, index) => {
    const updatedAttributes = [...activeAttributes];
    updatedAttributes[index] = attribute;
    setActiveAttributes(updatedAttributes);
  };

  const onSubmit = () => {
    const attributeIds = activeAttributes.filter(attr => attr.isActive).map(attr => attr.itemId);
    onAttributesUpdateSubmit(attributeIds);
  };

  const renderAttributeList = () => {
    return activeAttributes.map((item, index) => (
      <ModalItem
        key={item.itemId}
        item={item}
        title={item.name}
        index={index}
        onChecked={onAttributeChecked}
      />
    ));
  };

  const renderModalContent = () => {
    if (!response || isLoading) {
      return <LoadingSpinner />;
    }
    if (isError && error) {
      return <Message type="error" text={error} />;
    }
    return renderAttributeList();
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className={classes.root}>
        <div className={classes.itemsContainer}>{renderModalContent()}</div>
        <ThemeButton themeColor="primary" onClick={onSubmit} disabled={isLoading}>
          Confirm Attributes
        </ThemeButton>
      </div>
    </Modal>
  );
};

ActiveAttributesModal.propTypes = {
  attributes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dashboardData: PropTypes.shape({
    isCanned: PropTypes.bool,
    masterObjectId: PropTypes.number,
    masterObjectTypeId: PropTypes.number,
    objectTypeDetailId: PropTypes.number
  }),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAttributesUpdateSubmit: PropTypes.func.isRequired
};

ActiveAttributesModal.defaultProps = {
  isOpen: false,
  dashboardData: {}
};

export default ActiveAttributesModal;
